import React, { useEffect } from "react";
import {
  BlockHeroCompact,
  BlockSection,
  BlockText,
  BlockFigure,
  ImageSequence,
  RecipeRow,
  RecipeCell,
  RecipeBoard,
  projectPageShellClass,
  projectPageBottomClass,
  blockSectionBodyWrapClass,
} from "../../designSystem/projectBlocks";

const BASE = "/projects/Cowes Week Pavilion/images";

export default function CowesWeekPavilionPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const formGenSteps = [
    `${BASE}/1 1.png`,
    `${BASE}/2 1.png`,
    `${BASE}/3 1.png`,
    `${BASE}/4 1.png`,
    `${BASE}/5 1.png`,
    `${BASE}/6 1.png`,
  ];
  const formGenCaps = ["Step 01", "Step 02", "Step 03", "Step 04", "Step 05", "Step 06"];

  const tensileDetails = [
    `${BASE}/tensile detail 1.png`,
    `${BASE}/tensile detail 2.png`,
    `${BASE}/tensile detail 3.png`,
    `${BASE}/tensile detail 4.png`,
    `${BASE}/tensile detail 5.png`,
    `${BASE}/tensile detail 6.png`,
    `${BASE}/tensile detail7.png`,
    `${BASE}/tensile detail 8.png`,
  ];
  const tensileCaps = tensileDetails.map((_, i) => `Detail ${String(i + 1).padStart(2, "0")}`);

  return (
    <main className="min-h-screen bg-white dark:bg-black text-black dark:text-white transition-colors duration-500">
      <div className={`${projectPageShellClass} pt-[72px] ${projectPageBottomClass} box-border`}>
        <BlockHeroCompact
          title="Cowes Week Pavilion"
          description="A robotically fabricated tensile pavilion for the Cowes Week sailing regattas — a double-curved timber frame, carbon-fibre tensile wheels wound by a KUKA arm, and a CNC-cut membrane assembled in situ on the Solent."
          meta={["Isle of Wight, United Kingdom", "Rhino", "Grasshopper", "KUKA", "Kangaroo", "V-Ray", "2019"]}
        />

        <RecipeBoard>
          <RecipeRow>
            <RecipeCell span={12} className="!p-0 !border-r-0">
              <BlockFigure
                src={`${BASE}/renders/Card_Image9.png`}
                alt="Cowes Week Pavilion — hero render"
                ratio="banner"
                fit="cover"
              />
            </RecipeCell>
          </RecipeRow>
        </RecipeBoard>

        <BlockSection title="Design orientation">
          <BlockText
            className={blockSectionBodyWrapClass}
            paragraphs={[
              "Cowes Week is one of the oldest and largest annual sailing regattas in the world — an iconic fixture of the British sporting calendar, with 8,000 competitors and many more spectators on the Solent. The pavilion is sited on the northern shore of the Isle of Wight for wide views of the race and easy access from town; spectators can watch sailors approach the finish line.",
              "The design draws from the form of a traditional sail boat — borrowing its silhouette, structure, and materials — to provide a moderate shelter calibrated to the wind, sun, and sea conditions of the site.",
            ]}
          />

          <RecipeBoard>
            <RecipeRow>
              <RecipeCell span={8} className="!p-0">
                <BlockFigure src={`${BASE}/renders/Image1.png`} alt="Cowes Week Pavilion — exterior render" ratio="classic" fit="cover" />
              </RecipeCell>
              <RecipeCell span={4} className="!p-0 !border-r-0">
                <ImageSequence
                  images={formGenSteps}
                  captions={formGenCaps}
                  label="Form generation"
                  ratio="classic"
                />
              </RecipeCell>
            </RecipeRow>
          </RecipeBoard>
        </BlockSection>

        <BlockSection title="Structural system">
          <BlockText
            className={blockSectionBodyWrapClass}
            paragraphs={[
              "All geometric elements were generated algorithmically in Grasshopper. The double-curved timber frame is formed from twisting section variants that solve down to the required global geometry. The tensile support wheels combine a structural rim with carbon-fibre roving wound on a KUKA arm — holding the two structural layers together. The tensile membrane and the custom joints are CNC-machined. Every part is then shipped and assembled on site.",
            ]}
          />

          <RecipeBoard>
            <RecipeRow>
              <RecipeCell span={6} className="!p-0">
                <BlockFigure src={`${BASE}/tensile membrane geeration steps.png`} alt="Tensile membrane generation steps" ratio="classic" fit="contain" caption="Membrane generation" />
              </RecipeCell>
              <RecipeCell span={6} className="!p-0 !border-r-0">
                <BlockFigure src={`${BASE}/tensile membrane pattern.png`} alt="Tensile membrane pattern" ratio="classic" fit="contain" caption="Membrane pattern" />
              </RecipeCell>
            </RecipeRow>
            <RecipeRow>
              <RecipeCell span={6} className="!p-0">
                <BlockFigure src={`${BASE}/tensile wheels pattern.png`} alt="Tensile wheels winding pattern" ratio="classic" fit="contain" caption="Wheels pattern" />
              </RecipeCell>
              <RecipeCell span={6} className="!p-0 !border-r-0">
                <BlockFigure src={`${BASE}/one wheel.png`} alt="Single tensile wheel" ratio="classic" fit="contain" caption="Single wheel" />
              </RecipeCell>
            </RecipeRow>
          </RecipeBoard>
        </BlockSection>

        <BlockSection title="Tensile details">
          <RecipeBoard>
            <RecipeRow>
              <RecipeCell span={12} className="!p-0 !border-r-0">
                <ImageSequence
                  images={tensileDetails}
                  captions={tensileCaps}
                  label="Detail studies"
                  ratio="video"
                />
              </RecipeCell>
            </RecipeRow>
          </RecipeBoard>
        </BlockSection>

        <BlockSection title="Robotic fabrication">
          <BlockText
            className={blockSectionBodyWrapClass}
            paragraphs={[
              "The KUKA arm winds carbon fibre around each wheel along the toolpath defined inside Grasshopper — closing the gap between geometry and fabrication.",
            ]}
          />

          <RecipeBoard>
            <RecipeRow>
              <RecipeCell span={6} className="!p-0">
                <BlockFigure src={`${BASE}/robot 1.png`} alt="KUKA fabrication, frame 1" ratio="classic" fit="cover" />
              </RecipeCell>
              <RecipeCell span={6} className="!p-0 !border-r-0">
                <BlockFigure src={`${BASE}/robot 2.png`} alt="KUKA fabrication, frame 2" ratio="classic" fit="cover" />
              </RecipeCell>
            </RecipeRow>
          </RecipeBoard>
        </BlockSection>

        <BlockSection title="Renders and visualisations">
          <RecipeBoard>
            <RecipeRow>
              <RecipeCell span={6} className="!p-0">
                <BlockFigure src={`${BASE}/renders/Image3.png`} alt="Pavilion render 1" ratio="classic" fit="cover" />
              </RecipeCell>
              <RecipeCell span={6} className="!p-0 !border-r-0">
                <BlockFigure src={`${BASE}/renders/Image8.png`} alt="Pavilion render 2" ratio="classic" fit="cover" />
              </RecipeCell>
            </RecipeRow>
            <RecipeRow>
              <RecipeCell span={6} className="!p-0">
                <BlockFigure src={`${BASE}/renders/Image9.png`} alt="Pavilion render 3" ratio="classic" fit="cover" />
              </RecipeCell>
              <RecipeCell span={6} className="!p-0 !border-r-0">
                <BlockFigure src={`${BASE}/renders/Image10.png`} alt="Pavilion render 4" ratio="classic" fit="cover" />
              </RecipeCell>
            </RecipeRow>
          </RecipeBoard>
        </BlockSection>
      </div>
    </main>
  );
}
